import axios, { AxiosInstance } from 'axios';
import {
  FixtureType,
  FixtureFilters,
  FixturePaginationParams,
  FixtureFormData,
  FixtureBatchOperation,
  FixtureBatchResult,
  ApiResponse,
  FixtureErrorInfo,
  FixturePaginatedResponse,
} from '../types/fixture';
import { createSuccessResponse, createErrorResponse } from '../utils/responseTransformer';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';

/**
 * Service class for Fixture operations
 * Supports both Mock data and real API calls
 */
export class FixtureService {
  private static instance: FixtureService;
  
  // Static configuration to control whether to use mock data
  private static useMock: boolean = true;


  // Loading delays for mock data
  private static readonly LOADING_DELAYS = {
    LIST: 800,
    DETAIL: 600,
    CREATE: 1000,
    UPDATE: 800,
    DELETE: 600,
    BATCH_UPDATE: 1200,
  };

  /**
   * Get singleton instance
   */
  public static getInstance(): FixtureService {
    if (!FixtureService.instance) {
      FixtureService.instance = new FixtureService();
    }
    return FixtureService.instance;
  }

  /**
   * Set whether to use mock data
   * @param useMock Whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    FixtureService.useMock = useMock;
  }

  /**
   * Get current mock usage setting
   */
  public static getUseMock(): boolean {
    return FixtureService.useMock;
  }

  /**
   * Simulate API delay for development
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }


  /**
   * Get fixtures with filtering, pagination and sorting
   * @param filters Filter criteria
   * @param pagination Pagination parameters
   * @returns Promise with paginated fixture list
   */
  public async getFixtures(
    filters: FixtureFilters = {},
    pagination: FixturePaginationParams = { currentPage: 1, pageSize: 20 }
  ): Promise<ApiResponse<FixturePaginatedResponse<FixtureType>>> {
    try {
      if (FixtureService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(FixtureService.LOADING_DELAYS.LIST);

        // Import mock data dynamically to avoid circular dependencies
        const { getMockFixtureListResponse } = await import('../mock/fixtureData');
        return getMockFixtureListResponse(filters, pagination);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.FIXTURES.PAGE, {
          ...filters,
          currentPage: pagination.currentPage,
          pageSize: pagination.pageSize,
          sortBy: pagination.sortBy,
          sortOrder: pagination.sortOrder,
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching fixtures:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * Get fixture details by ID
   * @param id Fixture ID
   * @returns Promise with fixture details
   */
  public async getFixtureById(id: string): Promise<ApiResponse<FixtureType>> {
    try {
      if (FixtureService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(FixtureService.LOADING_DELAYS.DETAIL);

        // Import mock data dynamically
        const { getMockFixtureDetailResponse } = await import('../mock/fixtureData');
        return getMockFixtureDetailResponse(id);
      } else {
        // Call real API
        const response = await httpClient.get(API_ENDPOINTS.FIXTURES.QUERY_BY_ID + "/" + id);
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching fixture details:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * Create new fixture
   * @param fixtureData Fixture form data
   * @returns Promise with created fixture
   */
  public async createFixture(fixtureData: FixtureFormData): Promise<ApiResponse<FixtureType>> {
    try {
      if (FixtureService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(FixtureService.LOADING_DELAYS.CREATE);

        // Import mock data dynamically
        const { getMockFixtureCreateResponse } = await import('../mock/fixtureData');
        return getMockFixtureCreateResponse(fixtureData);
      } else {
        // Call real API
        // 准备JSON数据
        const jsonData: any = {
          typeName: fixtureData.typeName,
          typeCode: fixtureData.typeCode,
          ...fixtureData.dimensions,
          length: fixtureData.dimensions.depth,
          holeCount: fixtureData.holeCount,
          crossBeamCount: fixtureData.crossBeamCount,
          supportHold: fixtureData.supportHold,
          imageUrl: fixtureData.imageUrl?.[0]
        };
        
        // 添加可选字段
        if (fixtureData.description) {
          jsonData.description = fixtureData.description;
        }

        const response = await httpClient.post(API_ENDPOINTS.FIXTURES.CREATE, jsonData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error creating fixture:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * Update existing fixture
   * @param id Fixture ID
   * @param fixtureData Fixture form data
   * @returns Promise with updated fixture
   */
  public async updateFixture(id: string, fixtureData: FixtureFormData): Promise<ApiResponse<FixtureType>> {
    if (FixtureService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(FixtureService.LOADING_DELAYS.UPDATE);

        // Import mock data dynamically
        const { getMockFixtureUpdateResponse } = await import('../mock/fixtureData');
        return getMockFixtureUpdateResponse(id, fixtureData);
      } else {
        // Call real API
        // 准备JSON数据
        const jsonData: any = {
          typeName: fixtureData.typeName,
          typeCode: fixtureData.typeCode,
          ...fixtureData.dimensions,
          length: fixtureData.dimensions.depth,
          holeCount: fixtureData.holeCount,
          crossBeamCount: fixtureData.crossBeamCount,
          supportHold: fixtureData.supportHold,
          imageUrl: fixtureData.imageUrl?.[0]
        };
        
        // 添加可选字段
        if (fixtureData.description) {
          jsonData.description = fixtureData.description;
        }

        const response = await httpClient.post(API_ENDPOINTS.FIXTURES.UPDATE, jsonData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        return response.data;
      }
  }

  /**
   * Batch activate/deactivate fixtures
   * @param operation Batch operation details
   * @returns Promise with batch operation result
   */
  public async batchUpdateFixtures(operation: FixtureBatchOperation): Promise<ApiResponse<FixtureBatchResult>> {
    try {
      if (FixtureService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(FixtureService.LOADING_DELAYS.BATCH_UPDATE);

        // Import mock data dynamically
        const { getMockFixtureBatchUpdateResponse } = await import('../mock/fixtureData');
        return getMockFixtureBatchUpdateResponse(operation);
      } else {
        if (operation.operation === 'deactivate') {
          // Call real API
          const response = await httpClient.post(API_ENDPOINTS.FIXTURES.BATCH_DEACTIVE, operation);
          return response.data;
        }
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.FIXTURES.BATCH_ACTIVE, operation);
        return response.data;
      }
    } catch (error) {
      console.error('Error batch updating fixtures:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * Activate fixtures (batch operation)
   * @param ids Fixture IDs to activate
   * @returns Promise with batch operation result
   */
  public async activateFixtures(ids: string[]): Promise<ApiResponse<FixtureBatchResult>> {
    return this.batchUpdateFixtures({ ids, operation: 'activate' });
  }

  /**
   * Deactivate fixtures (batch operation)
   * @param ids Fixture IDs to deactivate
   * @returns Promise with batch operation result
   */
  public async deactivateFixtures(ids: string[]): Promise<ApiResponse<FixtureBatchResult>> {
    return this.batchUpdateFixtures({ ids, operation: 'deactivate' });
  }

  /**
   * Import fixture types from file
   * @param file Import file
   * @returns Promise with import result
   */
  public async importFixtureTypes(file: File): Promise<ApiResponse<any>> {
    try {
      if (FixtureService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(FixtureService.LOADING_DELAYS.CREATE);

        // Import mock data dynamically
        const { getMockFixtureImportResponse } = await import('../mock/fixtureData');
        return getMockFixtureImportResponse(file);
      } else {
        // Call real API
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await httpClient.post(API_ENDPOINTS.FIXTURES.IMPORT, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error importing fixture types:', error);
      return createErrorResponse(error.message, error.code);
    }
  }

  /**
   * Delete fixture by ID
   * @param id Fixture ID to delete
   * @returns Promise with deletion result
   */
  public async deleteFixture(id: string): Promise<ApiResponse<null>> {
    try {
      if (FixtureService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(FixtureService.LOADING_DELAYS.DELETE);

        // Import mock data dynamically
        const { getMockFixtureDeleteResponse } = await import('../mock/fixtureData');
        return getMockFixtureDeleteResponse(id);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.FIXTURES.DELETE, {id: id});
        return response.data;
      }
    } catch (error) {
      console.error('Error deleting fixture:', error);
      return createErrorResponse(error.message, error.code);
    }
  }
}

// Export singleton instance
export default FixtureService.getInstance();